import { useState, useCallback, useEffect, useRef } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Loader2, Shield, Image, Volume2, Video, Clock, DollarSign, AlertTriangle, Sparkles, Trash2, ChevronDown, ChevronUp, RotateCcw, Play, Square, Download } from "lucide-react";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Turnstile } from "@marsidev/react-turnstile";

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';
const API_URL = `${API_BASE}/api/studio/generate`;
const MAX_PROMPT_LENGTH = 500;
const MIN_PROMPT_LENGTH = 3;
const TURNSTILE_SITE_KEY = process.env.REACT_APP_TURNSTILE_SITE_KEY || '';

const PROVIDERS = [
  {
    id: 'fal-ai',
    label: 'Image Generation',
    description: 'AI-generated images via Fal.ai',
    icon: Image,
    models: [
      { id: 'fal-ai/flux/dev', label: 'Flux Dev (fast)' },
      { id: 'fal-ai/flux-pro/v1.1', label: 'Flux Pro (quality)' },
      { id: 'fal-ai/flux-realism', label: 'Flux Realism (photorealistic)' },
    ]
  },
  {
    id: 'elevenlabs',
    label: 'Voice Generation',
    description: 'Text-to-speech via ElevenLabs',
    icon: Volume2,
    models: [
      { id: 'eleven_multilingual_v2', label: 'Multilingual v2' },
      { id: 'eleven_flash_v2_5', label: 'Flash v2.5 (fastest)' },
      { id: 'eleven_turbo_v2_5', label: 'Turbo v2.5 (balanced)' },
    ]
  },
  {
    id: 'video',
    label: 'Video Generation',
    description: 'Image-to-video via AI (Veo 3.1, Runway, Luma)',
    icon: Video,
    models: [
      { id: 'veo', label: 'Veo 3.1 (Google, best quality)' },
      { id: 'runway', label: 'Runway Gen-3 Alpha' },
      { id: 'luma', label: 'Luma Dream Machine' },
    ]
  }
];

const VOICES = [
  { id: 'pNInz6obpgDQGcFmaJgB', label: 'Adam (deep, narrator)' },
  { id: '21m00Tcm4TlvDq8ikWAM', label: 'Rachel (calm, female)' },
  { id: 'EXAVITQu4vr4xnSDxMaL', label: 'Bella (soft, female)' },
  { id: 'ErXwobaYiN019PkySvjV', label: 'Antoni (warm, male)' },
  { id: 'MF3mGyEYCl7XYWbV9V6O', label: 'Elli (young, female)' },
  { id: 'TxGEqnHWrfWFTfGW9XjX', label: 'Josh (deep, male)' },
  { id: 'VR6AewLTigWG4xSOukaG', label: 'Arnold (strong, male)' },
  { id: 'yoZ06aMxZJJ28mfd3POQ', label: 'Sam (raspy, male)' },
];

const GALLERY_KEY = 'sovereign-studio-gallery';
const MAX_GALLERY_ITEMS = 20;

function loadGallery() {
  try {
    const raw = localStorage.getItem(GALLERY_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

function saveGallery(items) {
  try {
    localStorage.setItem(GALLERY_KEY, JSON.stringify(items.slice(0, MAX_GALLERY_ITEMS)));
  } catch { /* quota exceeded — silently ignore */ }
}

const PREVIEW_TEXT = 'Hello, this is a preview of my voice. How does it sound?';

function SovereignStudio() {
  const [selectedProvider, setSelectedProvider] = useState(null);
  const [prompt, setPrompt] = useState('');
  const [model, setModel] = useState('');
  const [voiceId, setVoiceId] = useState(VOICES[0].id);
  const [isLoading, setIsLoading] = useState(false);
  const [loadingStep, setLoadingStep] = useState('');
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [turnstileToken, setTurnstileToken] = useState(null);
  const [turnstileKey, setTurnstileKey] = useState(0);
  const [brandingLoaded, setBrandingLoaded] = useState(false);
  const [gallery, setGallery] = useState([]);
  const [galleryOpen, setGalleryOpen] = useState(false);
  const [previewItem, setPreviewItem] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewPlaying, setPreviewPlaying] = useState(false);
  const [previewCache, setPreviewCache] = useState({});
  const [downloadLoading, setDownloadLoading] = useState(false);
  const [sourceImage, setSourceImage] = useState('');
  const [videoDuration, setVideoDuration] = useState('8s');
  const [videoAspect, setVideoAspect] = useState('16:9');
  const previewAudioRef = useRef(null);

  useEffect(() => {
    const timer = setTimeout(() => setBrandingLoaded(true), 100);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    setGallery(loadGallery());
  }, []);

  // Cleanup audio on unmount
  useEffect(() => {
    return () => {
      if (previewAudioRef.current) {
        previewAudioRef.current.pause();
        previewAudioRef.current.onended = null;
        previewAudioRef.current.onerror = null;
        previewAudioRef.current = null;
      }
    };
  }, []);

  const handleProviderSelect = useCallback((providerId) => {
    const provider = PROVIDERS.find(p => p.id === providerId);
    setSelectedProvider(providerId);
    setResult(null);
    setError(null);
    setModel(provider?.models[0]?.id || '');
  }, []);

  const getStudioToken = () => sessionStorage.getItem('studio-auth-token') || '';

  const getOutputType = (resultData, provider) =>
    resultData?.output_type || (provider === 'fal-ai' ? 'image' : provider === 'video' ? 'video' : 'audio');

  const handleGenerate = useCallback(async () => {
    // Video: prompt is optional, but source_image is required
    if (selectedProvider === 'video') {
      if (!sourceImage || !sourceImage.match(/^https?:\/\/.+/)) {
        toast.error('Please enter a valid source image URL');
        return;
      }
    } else {
      if (prompt.length < MIN_PROMPT_LENGTH) {
        toast.error(`Prompt must be at least ${MIN_PROMPT_LENGTH} characters`);
        return;
      }
      if (prompt.length > MAX_PROMPT_LENGTH) {
        toast.error(`Prompt must be ${MAX_PROMPT_LENGTH} characters or less`);
        return;
      }
    }

    setIsLoading(true);
    setError(null);
    setResult(null);
    setLoadingStep('Verifying...');

    const timeoutMs = selectedProvider === 'video' ? 300000 : 120000;
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), timeoutMs);

    try {
      await new Promise(r => setTimeout(r, 800));
      setLoadingStep(
        selectedProvider === 'fal-ai'
          ? 'Generating image...'
          : selectedProvider === 'video'
            ? 'Generating video (this can take 30-60 seconds)...'
            : 'Generating audio...'
      );

      const body = {
        provider: selectedProvider,
        prompt,
        model,
        turnstile_token: turnstileToken,
      };

      if (selectedProvider === 'elevenlabs') {
        body.voice_id = voiceId;
      }

      if (selectedProvider === 'video') {
        body.source_image = sourceImage;
        body.video_provider = model; // model field maps to video_provider
        body.duration = videoDuration;
        body.aspect_ratio = videoAspect;
      }

      const response = await fetch(API_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Studio-Token': getStudioToken(),
        },
        body: JSON.stringify(body),
        signal: controller.signal,
      });

      setLoadingStep('Processing...');

      let data;
      try {
        const text = await response.text();
        data = JSON.parse(text);
      } catch {
        throw new Error(`Invalid response from server (${response.status})`);
      }

      if (response.status === 401) {
        sessionStorage.removeItem('studio-auth-token');
        window.location.reload();
        return;
      }

      if (!response.ok || !data.success) {
        throw new Error(data.error || `Request failed (${response.status})`);
      }

      setResult(data);
      const outputType = getOutputType(data.result, selectedProvider);
      const mediaUrl = data.result.audio_base64
        ? `data:audio/mpeg;base64,${data.result.audio_base64}`
        : (data.result.url && (data.result.url.startsWith('http') || data.result.url.startsWith('data:')) ? data.result.url : data.result.url);
      const entry = {
        id: data.tracking_id || Date.now().toString(),
        prompt,
        url: mediaUrl,
        provider: data.result.provider,
        model: data.result.model,
        output_type: outputType,
        cost_usd: data.result.cost_usd,
        generation_time_ms: data.generation_time_ms,
        voice_id: selectedProvider === 'elevenlabs' ? voiceId : undefined,
        source_image: selectedProvider === 'video' ? sourceImage : undefined,
        created_at: new Date().toISOString(),
      };
      setGallery(prev => {
        const next = [entry, ...prev].slice(0, MAX_GALLERY_ITEMS);
        saveGallery(next);
        return next;
      });
      toast.success('Media generated successfully!');
    } catch (err) {
      if (err.name === 'AbortError') {
        setError('Request timed out. Please try again.');
        toast.error('Request timed out');
      } else {
        setError(err.message || 'Something went wrong');
        toast.error('Generation failed');
      }
    } finally {
      clearTimeout(timeout);
      setIsLoading(false);
      setLoadingStep('');
      setTurnstileToken(null);
      setTurnstileKey(prev => prev + 1);
    }
  }, [prompt, selectedProvider, model, voiceId, turnstileToken, sourceImage, videoDuration, videoAspect]);

  const activeProvider = PROVIDERS.find(p => p.id === selectedProvider);
  const charProgress = (prompt.length / MAX_PROMPT_LENGTH) * 100;

  const clearGallery = useCallback(() => {
    setGallery([]);
    saveGallery([]);
    setPreviewItem(null);
    toast.success('Gallery cleared');
  }, []);

  const removeGalleryItem = useCallback((id) => {
    setGallery(prev => {
      const next = prev.filter(item => item.id !== id);
      saveGallery(next);
      return next;
    });
    if (previewItem?.id === id) setPreviewItem(null);
  }, [previewItem]);

  const handleRegenerate = useCallback((item) => {
    setSelectedProvider(item.provider);
    setModel(item.model);
    setPrompt(item.prompt);
    if (item.voice_id) setVoiceId(item.voice_id);
    setResult(null);
    setError(null);
    setPreviewItem(null);
    toast.success('Prompt loaded — hit Generate when ready');
    setTimeout(() => {
      document.querySelector('[data-testid="prompt-textarea"]')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 100);
  }, []);

  const handleAnimate = useCallback((item) => {
    setSelectedProvider('video');
    const videoProvider = PROVIDERS.find(p => p.id === 'video');
    setModel(videoProvider?.models[0]?.id || 'veo');
    setSourceImage(item.url);
    setPrompt('');
    setResult(null);
    setError(null);
    setPreviewItem(null);
    toast.success('Image loaded as source — describe the motion and hit Generate');
    setTimeout(() => {
      document.querySelector('[data-testid="prompt-textarea"]')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 100);
  }, []);

  const stopPreview = useCallback(() => {
    if (previewAudioRef.current) {
      previewAudioRef.current.pause();
      previewAudioRef.current.currentTime = 0;
    }
    setPreviewPlaying(false);
  }, []);

  const handlePreview = useCallback(async () => {
    // If already playing, stop
    if (previewPlaying) {
      stopPreview();
      return;
    }

    // If cached, play from cache
    if (previewCache[voiceId]) {
      const audio = new Audio(previewCache[voiceId]);
      previewAudioRef.current = audio;
      audio.onended = () => setPreviewPlaying(false);
      audio.onerror = () => { setPreviewPlaying(false); toast.error('Preview playback failed'); };
      setPreviewPlaying(true);
      audio.play();
      return;
    }

    // Generate preview via API
    setPreviewLoading(true);
    try {
      const response = await fetch(API_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Studio-Token': getStudioToken(),
        },
        body: JSON.stringify({
          provider: 'elevenlabs',
          prompt: PREVIEW_TEXT,
          model: 'eleven_multilingual_v2',
          voice_id: voiceId,
          turnstile_token: turnstileToken,
        }),
      });

      let data;
      try {
        const text = await response.text();
        data = JSON.parse(text);
      } catch {
        throw new Error('Invalid server response');
      }

      if (!data.success) throw new Error(data.error || 'Preview failed');

      const url = data.result.audio_base64
        ? `data:audio/mpeg;base64,${data.result.audio_base64}`
        : data.result.url;
      setPreviewCache(prev => ({ ...prev, [voiceId]: url }));

      const audio = new Audio(url);
      previewAudioRef.current = audio;
      audio.onended = () => setPreviewPlaying(false);
      audio.onerror = () => { setPreviewPlaying(false); toast.error('Preview playback failed'); };
      setPreviewPlaying(true);
      audio.play();
      toast.success(`Playing ${VOICES.find(v => v.id === voiceId)?.label.split(' (')[0]} preview`);
    } catch (err) {
      toast.error(err.message || 'Could not generate preview');
    } finally {
      setPreviewLoading(false);
    }
  }, [voiceId, previewPlaying, previewCache, turnstileToken, stopPreview]);

  // Stop preview when voice changes
  useEffect(() => {
    stopPreview();
  }, [voiceId, stopPreview]);

  const handleDownload = useCallback(async (url, outputType, audioBase64) => {
    setDownloadLoading(true);
    try {
      let blob;
      if (audioBase64) {
        const binary = atob(audioBase64);
        const bytes = new Uint8Array(binary.length);
        for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
        blob = new Blob([bytes], { type: 'audio/mpeg' });
      } else if (url && (url.startsWith('http') || url.startsWith('data:'))) {
        const response = await fetch(url);
        blob = await response.blob();
      } else {
        throw new Error('No downloadable media');
      }
      const ext = outputType === 'image' ? 'png' : outputType === 'video' ? 'mp4' : 'mp3';
      const filename = `sovereign-studio-${Date.now()}.${ext}`;
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(a.href);
      toast.success(`Downloaded as ${filename}`);
    } catch {
      toast.error('Download failed — the file may have expired');
    } finally {
      setDownloadLoading(false);
    }
  }, []);

  return (
    <div data-testid="sovereign-studio-page" className="min-h-screen py-8 px-4 sm:py-12 sm:px-6 lg:px-8">
      <div className="max-w-2xl mx-auto space-y-6">

        {/* Branding header */}
        <div className="space-y-2 text-center">
          <div
            data-testid="branding-badge"
            className={`inline-flex items-center gap-1.5 text-xs text-muted-foreground transition-all duration-500 ${
              brandingLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-2'
            }`}
          >
            <Sparkles className="h-3 w-3" />
            Sovereign Studio
          </div>
          <h1
            data-testid="page-title"
            className={`text-3xl sm:text-4xl font-bold tracking-tight transition-all duration-500 delay-100 ${
              brandingLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-2'
            }`}
          >
            Sovereign Studio
          </h1>
          <p
            data-testid="page-subtitle"
            className={`text-muted-foreground text-sm sm:text-base transition-all duration-500 delay-200 ${
              brandingLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-2'
            }`}
          >
            Generate media with full privacy. Your prompts, your results, nothing stored.
          </p>
        </div>

        {/* Provider selection */}
        <div data-testid="provider-selection" className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          {PROVIDERS.map((provider) => {
            const Icon = provider.icon;
            const isSelected = selectedProvider === provider.id;
            return (
              <Card
                key={provider.id}
                data-testid={`provider-card-${provider.id}`}
                className={`cursor-pointer transition-all duration-200 border shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 ${
                  isSelected
                    ? 'ring-2 ring-primary border-primary/30 shadow-lg'
                    : 'border-border hover:shadow-lg hover:border-primary/20'
                }`}
                onClick={() => handleProviderSelect(provider.id)}
                tabIndex={0}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleProviderSelect(provider.id); } }}
              >
                <CardHeader className="pb-2">
                  <div className="flex items-center gap-2">
                    <Icon className="h-5 w-5 text-muted-foreground" />
                    <CardTitle className="text-base">{provider.label}</CardTitle>
                  </div>
                </CardHeader>
                <CardContent>
                  <CardDescription>{provider.description}</CardDescription>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Generator Card */}
        {activeProvider && (
          <Card key={selectedProvider} data-testid="generator-card" className="border border-border shadow-md animate-entrance">
            <CardHeader className="animate-entrance-delay-1">
              <div className="flex items-center gap-2">
                <Sparkles className="h-5 w-5 text-muted-foreground" />
                <CardTitle>Your Prompt</CardTitle>
              </div>
              <CardDescription>
                {selectedProvider === 'fal-ai'
                  ? 'Describe the image you want to generate...'
                  : selectedProvider === 'video'
                    ? 'Animate a source image into video...'
                    : 'Enter the text you want to hear spoken aloud...'}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4 animate-entrance-delay-2">
              <div className="space-y-2">
                <Textarea
                  data-testid="prompt-textarea"
                  placeholder={
                    selectedProvider === 'fal-ai'
                      ? 'A serene mountain landscape at sunset with vibrant colors...'
                      : selectedProvider === 'video'
                        ? 'Slow camera dolly forward, gentle wind moving through the scene...'
                        : 'Hello, welcome to Sovereign Studio...'
                  }
                  value={prompt}
                  onChange={(e) => setPrompt(e.target.value)}
                  maxLength={MAX_PROMPT_LENGTH}
                  rows={4}
                  disabled={isLoading}
                  className="resize-none"
                />
                <div className="flex items-center justify-between text-xs text-muted-foreground">
                  <span data-testid="char-counter">{prompt.length} / {MAX_PROMPT_LENGTH}</span>
                </div>
                <div
                  data-testid="char-progress-bar"
                  className="h-1 w-full rounded-full bg-muted overflow-hidden"
                >
                  <div
                    className={`h-full transition-all duration-300 rounded-full ${
                      charProgress > 90 ? 'bg-destructive' : 'bg-primary'
                    }`}
                    style={{ width: `${Math.min(charProgress, 100)}%` }}
                  />
                </div>
              </div>

              <div className="space-y-1.5">
                <label className="text-sm font-medium" htmlFor="model-select">Model</label>
                <Select
                  value={model}
                  onValueChange={setModel}
                  disabled={isLoading}
                >
                  <SelectTrigger data-testid="model-select-trigger" id="model-select">
                    <SelectValue placeholder="Select a model" />
                  </SelectTrigger>
                  <SelectContent>
                    {activeProvider.models.map((m) => (
                      <SelectItem key={m.id} value={m.id} data-testid={`model-option-${m.id}`}>
                        {m.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {selectedProvider === 'video' && (
                <>
                  <div className="space-y-1.5">
                    <label className="text-sm font-medium" htmlFor="source-image-input">Source Image URL</label>
                    <Input
                      data-testid="source-image-input"
                      id="source-image-input"
                      type="url"
                      placeholder="https://example.com/image.png"
                      value={sourceImage}
                      onChange={(e) => setSourceImage(e.target.value)}
                      disabled={isLoading}
                    />
                    {sourceImage && sourceImage.match(/^https?:\/\/.+/) && (
                      <img src={sourceImage} alt="Source preview" className="w-24 h-24 object-cover rounded-lg border" />
                    )}
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1.5">
                      <label className="text-sm font-medium" htmlFor="duration-select">Duration</label>
                      <Select value={videoDuration} onValueChange={setVideoDuration} disabled={isLoading}>
                        <SelectTrigger data-testid="duration-select-trigger" id="duration-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="4s">4 seconds</SelectItem>
                          <SelectItem value="6s">6 seconds</SelectItem>
                          <SelectItem value="8s">8 seconds</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-1.5">
                      <label className="text-sm font-medium" htmlFor="aspect-select">Aspect Ratio</label>
                      <Select value={videoAspect} onValueChange={setVideoAspect} disabled={isLoading}>
                        <SelectTrigger data-testid="aspect-select-trigger" id="aspect-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="16:9">16:9 (Landscape)</SelectItem>
                          <SelectItem value="9:16">9:16 (Portrait)</SelectItem>
                          <SelectItem value="1:1">1:1 (Square)</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </>
              )}

              {selectedProvider === 'elevenlabs' && (
                <div className="space-y-1.5">
                  <label className="text-sm font-medium" htmlFor="voice-select">Voice</label>
                  <div className="flex gap-2">
                    <Select
                      value={voiceId}
                      onValueChange={setVoiceId}
                      disabled={isLoading}
                    >
                      <SelectTrigger data-testid="voice-select-trigger" id="voice-select" className="flex-1">
                        <SelectValue placeholder="Select a voice" />
                      </SelectTrigger>
                      <SelectContent>
                        {VOICES.map((v) => (
                          <SelectItem key={v.id} value={v.id} data-testid={`voice-option-${v.id}`}>
                            {v.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Button
                      data-testid="voice-preview-button"
                      type="button"
                      variant="outline"
                      size="icon"
                      className="shrink-0 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
                      onClick={handlePreview}
                      disabled={isLoading || previewLoading}
                      title={previewPlaying ? 'Stop preview' : 'Preview voice'}
                    >
                      {previewLoading ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : previewPlaying ? (
                        <Square className="h-4 w-4" />
                      ) : (
                        <Play className="h-4 w-4" />
                      )}
                    </Button>
                  </div>
                  {previewPlaying && (
                    <p data-testid="voice-preview-status" className="text-xs text-primary animate-fade-in-up">
                      Playing preview...
                    </p>
                  )}
                  {previewCache[voiceId] && !previewPlaying && !previewLoading && (
                    <p data-testid="voice-preview-cached" className="text-xs text-muted-foreground">
                      Preview cached — click play to listen again
                    </p>
                  )}
                </div>
              )}

              {TURNSTILE_SITE_KEY && (
                <div data-testid="turnstile-widget" className="flex justify-center">
                  <Turnstile
                    key={turnstileKey}
                    siteKey={TURNSTILE_SITE_KEY}
                    onSuccess={setTurnstileToken}
                    onError={() => setTurnstileToken(null)}
                    onExpire={() => setTurnstileToken(null)}
                  />
                </div>
              )}

              <Button
                data-testid="generate-button"
                className="w-full sm:w-auto focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
                size="lg"
                onClick={handleGenerate}
                disabled={
                  isLoading ||
                  (selectedProvider !== 'video' && prompt.length < MIN_PROMPT_LENGTH) ||
                  (selectedProvider === 'video' && (!sourceImage || !sourceImage.match(/^https?:\/\/.+/))) ||
                  (TURNSTILE_SITE_KEY && !turnstileToken)
                }
              >
                {isLoading ? (
                  <>
                    <Loader2 className="animate-spin" />
                    <span>{loadingStep}</span>
                  </>
                ) : (
                  'Generate'
                )}
              </Button>

              <div data-testid="security-indicator" className="flex items-center gap-1.5 text-xs text-muted-foreground">
                <Shield className="h-3 w-3" />
                <span>Protected by rate limiting &amp; bot defense</span>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Error state */}
        {error && (
          <div className="animate-fade-in-up" data-testid="error-alert">
            <Alert variant="destructive">
              <AlertTriangle className="h-4 w-4" />
              <AlertTitle>Something went wrong</AlertTitle>
              <AlertDescription className="mt-2">
                <p>{error}</p>
                <Button
                  data-testid="try-again-button"
                  variant="outline"
                  size="sm"
                  className="mt-3"
                  onClick={() => {
                    setError(null);
                    setTurnstileKey(prev => prev + 1);
                  }}
                >
                  Try Again
                </Button>
              </AlertDescription>
            </Alert>
          </div>
        )}

        {/* Result Card */}
        {result && result.success && (
          <div className="animate-fade-in-up" data-testid="result-card-wrapper">
            <Card className="border border-border shadow-md">
              <CardHeader>
                <CardTitle>Result</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {getOutputType(result.result, selectedProvider) === 'image' && (
                  <img data-testid="result-image" src={result.result.url} alt={result.prompt || 'Generated image'} className="w-full rounded-lg max-w-full" />
                )}
                {getOutputType(result.result, selectedProvider) === 'video' && (
                  <video data-testid="result-video" controls preload="metadata" className="w-full rounded-lg">
                    <source src={result.result.url} type="video/mp4" />
                    Your browser does not support the video element.
                  </video>
                )}
                {getOutputType(result.result, selectedProvider) === 'audio' && (
                  <audio data-testid="result-audio" controls className="w-full">
                    <source src={
                      result.result.audio_base64
                        ? `data:audio/mpeg;base64,${result.result.audio_base64}`
                        : (result.result.url && (result.result.url.startsWith('http') || result.result.url.startsWith('data:')))
                          ? result.result.url
                          : undefined
                    } />
                    Your browser does not support the audio element.
                  </audio>
                )}

                <div data-testid="result-badges" className="flex flex-wrap gap-2">
                  <Badge variant="secondary" data-testid="badge-provider">
                    {result.result.provider}
                  </Badge>
                  <Badge variant="secondary" data-testid="badge-model">
                    {result.result.model}
                  </Badge>
                  {selectedProvider === 'elevenlabs' && (
                    <Badge variant="secondary" data-testid="badge-result-voice">
                      <Volume2 className="h-3 w-3 mr-1" />
                      {VOICES.find(v => v.id === voiceId)?.label.split(' (')[0] || 'Custom'}
                    </Badge>
                  )}
                  {result.generation_time_ms && (
                    <Badge variant="outline" className="text-green-600 border-green-200" data-testid="badge-time">
                      <Clock className="h-3 w-3 mr-1" />
                      {(result.generation_time_ms / 1000).toFixed(1)}s
                    </Badge>
                  )}
                  {result.result.cost_usd != null && (
                    <Badge variant="outline" data-testid="badge-cost">
                      <DollarSign className="h-3 w-3 mr-1" />
                      ~${result.result.cost_usd.toFixed(3)}
                    </Badge>
                  )}
                </div>

                <Button
                  data-testid="download-result-button"
                  variant="outline"
                  size="sm"
                  className="w-full sm:w-auto"
                  onClick={() => handleDownload(result.result.url, getOutputType(result.result, selectedProvider), result.result.audio_base64)}
                  disabled={downloadLoading}
                >
                  {downloadLoading ? (
                    <Loader2 className="h-4 w-4 animate-spin mr-1.5" />
                  ) : (
                    <Download className="h-4 w-4 mr-1.5" />
                  )}
                  {downloadLoading ? 'Downloading...' : 'Download'}
                </Button>

                <p data-testid="result-expiry-note" className="text-xs text-muted-foreground">
                  This result is temporary and will expire (Fal CDN: ~1 hour)
                </p>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Gallery */}
        {gallery.length > 0 && (
          <div className="animate-fade-in-up" data-testid="gallery-section">
            <Card className="border border-border shadow-md">
              <CardHeader
                className="cursor-pointer select-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 rounded-t-xl"
                onClick={() => setGalleryOpen(prev => !prev)}
                data-testid="gallery-toggle"
                tabIndex={0}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setGalleryOpen(prev => !prev); } }}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Clock className="h-5 w-5 text-muted-foreground" />
                    <CardTitle>Recent Generations</CardTitle>
                    <Badge variant="secondary" data-testid="gallery-count">{gallery.length}</Badge>
                  </div>
                  {galleryOpen ? (
                    <ChevronUp className="h-4 w-4 text-muted-foreground" />
                  ) : (
                    <ChevronDown className="h-4 w-4 text-muted-foreground" />
                  )}
                </div>
                <CardDescription>
                  {galleryOpen ? 'Click a thumbnail to preview' : 'Expand to browse your history'}
                </CardDescription>
              </CardHeader>

              {galleryOpen && (
                <CardContent className="space-y-4">
                  <div
                    data-testid="gallery-grid"
                    className="grid grid-cols-3 sm:grid-cols-4 gap-3"
                  >
                    {gallery.map((item) => (
                      <div
                        key={item.id}
                        data-testid={`gallery-item-${item.id}`}
                        className={`group relative rounded-lg overflow-hidden border transition-all duration-200 cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 ${
                          previewItem?.id === item.id
                            ? 'ring-2 ring-primary border-primary/30 shadow-md'
                            : 'hover:shadow-md hover:ring-2 hover:ring-primary/30'
                        }`}
                        onClick={() => setPreviewItem(previewItem?.id === item.id ? null : item)}
                        tabIndex={0}
                        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setPreviewItem(previewItem?.id === item.id ? null : item); } }}
                      >
                        {item.output_type === 'image' ? (
                          <img
                            src={item.url}
                            alt={item.prompt}
                            className="w-full aspect-square object-cover"
                            loading="lazy"
                          />
                        ) : item.output_type === 'video' ? (
                          <div className="w-full aspect-square bg-muted flex flex-col items-center justify-center gap-1 relative">
                            <Video className="h-6 w-6 text-muted-foreground" />
                            <span className="text-[10px] text-muted-foreground px-1 text-center line-clamp-2">
                              {(item.prompt || 'Video').slice(0, 40)}
                            </span>
                          </div>
                        ) : (
                          <div className="w-full aspect-square bg-muted flex flex-col items-center justify-center gap-1">
                            <Volume2 className="h-6 w-6 text-muted-foreground" />
                            <span className="text-[10px] text-muted-foreground px-1 text-center line-clamp-2">
                              {item.prompt.slice(0, 40)}
                            </span>
                          </div>
                        )}
                        <button
                          data-testid={`gallery-remove-${item.id}`}
                          className="absolute top-1 right-1 p-1 rounded-full bg-background/80 opacity-0 group-hover:opacity-100 transition-opacity"
                          onClick={(e) => { e.stopPropagation(); removeGalleryItem(item.id); }}
                          aria-label="Remove"
                        >
                          <Trash2 className="h-3 w-3 text-destructive" />
                        </button>
                      </div>
                    ))}
                  </div>

                  {/* Preview panel */}
                  {previewItem && (
                    <div data-testid="gallery-preview" className="animate-fade-in-up space-y-3 pt-2 border-t">
                      {previewItem.output_type === 'image' ? (
                        <img
                          data-testid="gallery-preview-image"
                          src={previewItem.url}
                          alt={previewItem.prompt}
                          className="w-full rounded-lg"
                        />
                      ) : previewItem.output_type === 'video' ? (
                        <video data-testid="gallery-preview-video" controls preload="metadata" className="w-full rounded-lg">
                          <source src={previewItem.url} type="video/mp4" />
                        </video>
                      ) : (
                        <audio data-testid="gallery-preview-audio" controls className="w-full">
                          <source src={
                            previewItem.url && (previewItem.url.startsWith('http') || previewItem.url.startsWith('data:'))
                              ? previewItem.url
                              : undefined
                          } />
                        </audio>
                      )}
                      <p data-testid="gallery-preview-prompt" className="text-sm text-muted-foreground italic">
                        &ldquo;{previewItem.prompt}&rdquo;
                      </p>
                      <div className="flex flex-wrap gap-2">
                        <Badge variant="secondary">{previewItem.provider}</Badge>
                        <Badge variant="secondary">{previewItem.model}</Badge>
                        {previewItem.voice_id && (
                          <Badge variant="secondary" data-testid="badge-voice">
                            <Volume2 className="h-3 w-3 mr-1" />
                            {VOICES.find(v => v.id === previewItem.voice_id)?.label.split(' (')[0] || 'Custom'}
                          </Badge>
                        )}
                        {previewItem.generation_time_ms && (
                          <Badge variant="outline" className="text-green-600 border-green-200">
                            <Clock className="h-3 w-3 mr-1" />
                            {(previewItem.generation_time_ms / 1000).toFixed(1)}s
                          </Badge>
                        )}
                        {previewItem.cost_usd != null && (
                          <Badge variant="outline">
                            <DollarSign className="h-3 w-3 mr-1" />
                            ~${previewItem.cost_usd.toFixed(3)}
                          </Badge>
                        )}
                      </div>
                      <p className="text-[11px] text-muted-foreground">
                        {new Date(previewItem.created_at).toLocaleString()}
                      </p>
                      <div className="flex flex-wrap gap-2">
                        <Button
                          data-testid="regenerate-button"
                          variant="outline"
                          size="sm"
                          onClick={() => handleRegenerate(previewItem)}
                        >
                          <RotateCcw className="h-3.5 w-3.5 mr-1.5" />
                          Re-generate
                        </Button>
                        {previewItem.output_type === 'image' && previewItem.url && previewItem.url.startsWith('http') && (
                          <Button
                            data-testid="animate-button"
                            variant="outline"
                            size="sm"
                            onClick={() => handleAnimate(previewItem)}
                          >
                            <Video className="h-3.5 w-3.5 mr-1.5" />
                            Animate
                          </Button>
                        )}
                        <Button
                          data-testid="download-gallery-button"
                          variant="outline"
                          size="sm"
                          onClick={() => handleDownload(previewItem.url, previewItem.output_type)}
                          disabled={downloadLoading}
                        >
                          {downloadLoading ? (
                            <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" />
                          ) : (
                            <Download className="h-3.5 w-3.5 mr-1.5" />
                          )}
                          Download
                        </Button>
                      </div>
                    </div>
                  )}

                  <div className="flex justify-end">
                    <Button
                      data-testid="clear-gallery-button"
                      variant="ghost"
                      size="sm"
                      className="text-destructive hover:text-destructive hover:bg-destructive/10"
                      onClick={clearGallery}
                    >
                      <Trash2 className="h-3.5 w-3.5 mr-1" />
                      Clear all
                    </Button>
                  </div>
                </CardContent>
              )}
            </Card>
          </div>
        )}

        {/* Footer */}
        <div data-testid="footer" className="text-center text-xs text-muted-foreground pt-4">
          Powered by DMI &middot; Privacy by Architecture &middot; No data stored
        </div>
      </div>
    </div>
  );
}

export default SovereignStudio;
